<template>
  <modal
    elem-id="modal_staff_subscriptions_extend"
    :title="t('Estender assinatura')"
    :show="isVisible"
    size="small"
    :close-label="t('Fechar')"
    :save-label="t('Estender')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving || !days"
    save-test-id="staff-subscriptions-extend-submit"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="extend-context">
        <div class="context-field">
          <span class="context-label">{{ t('Franquia') }}</span>
          <span class="context-value" data-testid="staff-subscriptions-extend-franchise">{{ currentRow?.franchise_name }}</span>
        </div>
        <div class="context-field">
          <span class="context-label">{{ t('Plano') }}</span>
          <span class="context-value" data-testid="staff-subscriptions-extend-plan">{{ currentRow?.plan_name ?? t('Sem assinatura') }}</span>
        </div>
        <div class="context-field">
          <span class="context-label">{{ t('Vence em') }}</span>
          <span class="context-value" data-testid="staff-subscriptions-extend-due">{{ dueDisplay }}</span>
        </div>
      </div>
      <div class="extend-input">
        <input-number
          v-model="days"
          elem-id="staff-subscriptions-extend-days"
          :label="t('Dias')"
          data-testid="staff-subscriptions-extend-days-input"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import Modal from '../../../ui/Modal.vue'
import InputNumber from '../../../inputs/InputNumber.vue'
import type { Subscriptions_AdminSubscriptionRow } from '../../../contracts/Subscriptions_AdminSubscriptionRow'

interface ModalOpenPayload {
  row?: Subscriptions_AdminSubscriptionRow
}

const { t } = useI18n()
const { extend } = useSubscriptionAdmin()
const { formatDate, fromApiDateToDate } = useDateFormatter()

const isVisible = ref(false)
const isSaving = ref(false)
const currentRow = ref<Subscriptions_AdminSubscriptionRow | null>(null)
const days = ref<number | null>(null)

const dueDisplay = computed((): string => {
  const raw = currentRow.value?.current_period_end ?? currentRow.value?.grace_until ?? null
  if (!raw) return ''
  const d = fromApiDateToDate(raw)
  return formatDate(d)
})

function openModal(payload?: ModalOpenPayload): void {
  log('StaffSubscriptionsExtendModal', 'openModal', 'Abrindo modal', payload)
  currentRow.value = payload?.row ?? null
  days.value = null
  isVisible.value = true
}

function closeModal(): void {
  log('StaffSubscriptionsExtendModal', 'closeModal', 'Fechando modal')
  isVisible.value = false
  currentRow.value = null
  days.value = null
}

async function handleSave(): Promise<void> {
  const row = currentRow.value
  if (!row || !days.value) return
  isSaving.value = true
  try {
    // Existing subscription → extend it; franchise WITHOUT subscription
    // (synthetic row, subscription_id null) → grant a grace window by franquia_id.
    if (row.subscription_id != null) {
      await extend({ subscription_id: row.subscription_id, days: days.value })
    } else {
      await extend({ franquia_id: row.franquia_id, days: days.value })
    }
    log('StaffSubscriptionsExtendModal', 'handleSave', 'Assinatura estendida')
    eventBus.emit('globalMessage.success', t('Assinatura estendida com sucesso'))
    // Keep the legacy event chain unchanged per S4
    eventBus.emit('staffSubscription.extended')
    eventBus.emit('staffSubscriptionExtend.saved')
    closeModal()
    eventBus.emit('staffSubscriptions.refresh')
  } catch (err) {
    log('StaffSubscriptionsExtendModal', 'handleSave', 'Erro ao estender assinatura', err)
    eventBus.emit('globalMessage.error', t('Erro ao estender assinatura'))
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.staffSubscriptionsExtend.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.staffSubscriptionsExtend.open', openModal)
})
</script>

<style scoped>
.extend-context {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.context-field {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.context-label {
  font-weight: 600;
  min-width: 80px;
}

.extend-input {
  margin-top: 0.5rem;
}
</style>
